import { colors } from '@kmns-internal/furikake'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { Box } from '@mui/material'
import { RichTreeView } from '@mui/x-tree-view/RichTreeView'
import { TreeItem, type TreeItemProps } from '@mui/x-tree-view/TreeItem'
import {
  type PlaceNode,
  PlaceNodeTypeEnum,
} from '@ulysses-inc/harami_api_client'
import type React from 'react'
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import PlaceGroupSVG from 'src/assets/icons/place_groups.svg?react'

// 現行仕様
// * 現場を持つ現場グループはクリックできる
// * 現場を持たない現場グループはクリックできない
// * 現場は重複して別のところに置くこともできる
// * 重複した現場を選択/解除したら別の現場グループ配下にある現場も選択/解除される
// * API で送受信される値は現場グループをのぞいた現場の uuid のみ、現場グループとの整合性はフロントエンドで組み合わせて表示する

// MUI RichTreeView用のデータ構造
type TreeDataItem = {
  id: string
  label: string
  children?: TreeDataItem[]
  uuid: string
  type: PlaceNodeTypeEnum
}

type CheckedKeys =
  | (string | number)[]
  | {
      checked: (string | number)[]
      halfChecked: (string | number)[]
    }
interface OwnProps {
  placeNodes: Array<PlaceNode>
  checkedKeys: CheckedKeys
  onCheck: (checkedKeys: CheckedKeys) => void
}

/**
 * スケジュール詳細画面の現場・現場グループ選択ツリー
 */
const PlaceGroupsTree: React.FC<OwnProps> = (props: OwnProps) => {
  const nextKeyIdRef = useRef<{ [key: string]: number }>({})
  // typeを保持するためのMap
  const treeItemTypeMapRef = useRef(new Map<string, PlaceNodeTypeEnum>())

  // 現場・現場グループのチェック状態
  const [checkList, setCheckList] = useState<string[]>([])
  // ツリーデータ
  const [treeData, setTreeData] = useState<TreeDataItem[]>([])

  // カスタムTreeItemコンポーネント
  const CustomTreeItem = forwardRef<HTMLLIElement, TreeItemProps>(
    // eslint-disable-next-line react-hooks/refs -- レンダー中のref参照は既存設計のため、リファクタリング時に修正する
    function CustomTreeItem(itemProps, ref) {
      const { label, itemId, ...other } = itemProps

      // itemIdからtypeを取得
      const nodeType = itemId
        ? treeItemTypeMapRef.current.get(itemId)
        : undefined
      const icon =
        nodeType === PlaceNodeTypeEnum.PlaceGroup ? (
          <PlaceGroupSVG
            style={{
              width: 16,
              height: 16,
              marginRight: 8,
              color: colors.action.active,
            }}
          />
        ) : (
          <LocationOnIcon
            fontSize="small"
            sx={{ mr: 1, color: colors.action.active }}
          />
        )

      return (
        <TreeItem
          ref={ref}
          itemId={itemId}
          {...other}
          label={
            <Box
              sx={{ display: 'flex', alignItems: 'center' }}
              onClick={event => {
                // チェックボックス以外の部分をクリックしたときに開閉しないようにする
                event.stopPropagation()
              }}
            >
              {icon}
              {label}
            </Box>
          }
        />
      )
    },
  )

  const generateTreeData = useCallback((nodes: PlaceNode[]): TreeDataItem[] => {
    return nodes.map(node => {
      // idは一意である必要があるが、同じ現場の場合uuidが同じ値をとる
      // => 同じ現場内でインデックスを振り一意になるように設定する
      const currentId = nextKeyIdRef.current[node.uuid]
      nextKeyIdRef.current[node.uuid] =
        currentId === undefined ? 0 : currentId + 1

      const itemId = `${node.uuid}/${nextKeyIdRef.current[node.uuid]}`
      treeItemTypeMapRef.current.set(itemId, node.type)

      switch (node.type) {
        case PlaceNodeTypeEnum.PlaceGroup: {
          const groupName = node.placeGroup?.name ?? ''
          return {
            id: itemId,
            uuid: node.uuid,
            type: node.type,
            label: groupName,
            // eslint-disable-next-line react-hooks/immutability
            children: node.nodes ? generateTreeData(node.nodes) : undefined,
          }
        }

        case PlaceNodeTypeEnum.Place: {
          const placeName = node.place?.name ?? ''
          return {
            id: itemId,
            uuid: node.uuid,
            type: node.type,
            label: placeName,
          }
        }

        default: {
          throw new Error('不正な現場のデータです')
        }
      }
    })
  }, [])

  // 2. Props からの初期化とデータ生成を一度に行う
  // checkedKeys の実質的な変化を検知するためのメモ
  const checkedKeysString = JSON.stringify(props.checkedKeys)

  useEffect(() => {
    nextKeyIdRef.current = {}
    treeItemTypeMapRef.current.clear()

    // 1. まずツリーデータを生成
    const generatedTreeData = generateTreeData(props.placeNodes)

    // 2. その生成データ（generatedTreeData）を直接使って初期チェックを計算
    const initialIds: string[] = []
    const checkedUuids = props.checkedKeys.toString().split(',')

    const computeInitialChecks = (nodes: TreeDataItem[]): boolean => {
      let allSiblingsChecked = true

      nodes.forEach(node => {
        let isNodeChecked = false

        if (node.type === PlaceNodeTypeEnum.Place) {
          isNodeChecked = checkedUuids.includes(node.uuid)
        } else if (node.children && node.children.length > 0) {
          isNodeChecked = computeInitialChecks(node.children)
        }

        if (isNodeChecked) {
          initialIds.push(node.id)
        } else {
          allSiblingsChecked = false
        }
      })

      return allSiblingsChecked
    }

    if (generatedTreeData.length > 0) {
      computeInitialChecks(generatedTreeData)
    }

    // 3. 両方の State を同時に更新
    // これにより「ノードはあるがチェック ID がない」という瞬間を最小限にします
    // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
    setTreeData(generatedTreeData)
    setCheckList(initialIds)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.placeNodes, checkedKeysString, generateTreeData])

  const getUuidFromItemId = (id: string): string | null => {
    const uuid = id.split('/')[0]
    return uuid ?? null
  }

  const onCheckPreprocess = (selectedIds: string[]) => {
    // ID 単位で「何が追加されたか」「何が削除されたか」を正確に特定する
    const prevIds = new Set(checkList)

    // 今回「追加」された ID を特定
    const addedIdsRaw = selectedIds.filter(id => !prevIds.has(id))

    // 有効な追加 ID のみを選別する
    // 「そのノード、またはその子孫に Place が一つも存在しない」場合は無視する
    const addedIds = addedIdsRaw.filter(id => {
      let hasPlace = false
      const findPlace = (nodes: TreeDataItem[]) => {
        for (const node of nodes) {
          if (node.id === id) {
            // 対象ノードを見つけたら、その子孫をスキャン
            const scan = (n: TreeDataItem) => {
              if (n.type === PlaceNodeTypeEnum.Place) hasPlace = true
              if (!hasPlace && n.children) n.children.forEach(scan)
            }
            scan(node)
            return true
          }
          if (node.children && findPlace(node.children)) return true
        }
        return false
      }
      findPlace(treeData)
      return hasPlace
    })

    // もし追加しようとした ID がすべて無効（Placeを持たないグループ）だった場合、
    // 状態を更新せずに現在の checkList を維持して終了する
    if (addedIdsRaw.length > 0 && addedIds.length === 0) {
      setCheckList([...checkList]) // 再描画を促してUIを戻す

      // 現在のチェック状態と treeData から Place(uuid) だけを抽出して返す
      const checkedIdSet = new Set(checkList)
      const placeUuidSet = new Set<string>()

      const collectCheckedPlaces = (nodes: TreeDataItem[]) => {
        nodes.forEach(node => {
          if (
            node.type === PlaceNodeTypeEnum.Place &&
            checkedIdSet.has(node.id)
          ) {
            const uuid = getUuidFromItemId(node.id)
            if (uuid) placeUuidSet.add(uuid)
          }
          if (node.children) {
            collectCheckedPlaces(node.children)
          }
        })
      }

      collectCheckedPlaces(treeData)
      return Array.from(placeUuidSet)
    }

    const currentIds = new Set(selectedIds)
    const removedIds = checkList.filter(id => !currentIds.has(id))

    // 変化した UUID を特定
    // ... (addedUuids を追加された filtered な addedIds から生成するように変更)
    const addedUuids = new Set(
      addedIds
        .map(getUuidFromItemId)
        .filter((uuid): uuid is string => Boolean(uuid)),
    )
    const removedUuids = new Set(
      removedIds
        .map(getUuidFromItemId)
        .filter((uuid): uuid is string => Boolean(uuid)),
    )

    // 最終的な UUID マスターリストを更新
    // 前回の UUID セットをベースに、追加されたものを足し、削除されたものを引く
    const prevUuids = new Set(
      checkList
        .map(getUuidFromItemId)
        .filter((uuid): uuid is string => Boolean(uuid)),
    )
    const finalActiveUuids = new Set(prevUuids)

    // 追加された UUID を反映
    addedUuids.forEach(uuid => finalActiveUuids.add(uuid))
    // 削除された UUID を反映（重複していても、1箇所消されたら全消しする意思として扱う）
    removedUuids.forEach(uuid => finalActiveUuids.delete(uuid))

    // ツリー全体を走査して ID リストを再構成（同期）
    const nextCheckList = new Set<string>()
    const finalUuids = new Set<string>()

    const syncAllInstances = (nodes: TreeDataItem[]) => {
      nodes.forEach(node => {
        if (finalActiveUuids.has(node.uuid)) {
          nextCheckList.add(node.id)
          if (node.type === PlaceNodeTypeEnum.Place) {
            finalUuids.add(node.uuid)
          }
        }
        if (node.children) syncAllInstances(node.children)
      })
    }

    syncAllInstances(treeData)

    const finalCheckListArray = Array.from(nextCheckList)
    setCheckList(finalCheckListArray)

    return Array.from(finalUuids)
  }

  const handleSelectedItemsChange = (
    _event: React.SyntheticEvent | null,
    itemIds: string[] | string | null,
  ) => {
    if (!itemIds) {
      setCheckList([])
      props.onCheck([])
      return
    }
    const itemIdsArray = Array.isArray(itemIds) ? itemIds : [itemIds]

    // MUI が生成した complete なリストを使って API 用データを抽出
    const resultUuids = onCheckPreprocess(itemIdsArray)
    props.onCheck(resultUuids)
  }

  return (
    <>
      {props.placeNodes.length > 0 && (
        <RichTreeView
          items={treeData}
          checkboxSelection
          multiSelect={true}
          selectedItems={checkList}
          onSelectedItemsChange={handleSelectedItemsChange}
          selectionPropagation={{
            parents: true,
            descendants: true,
          }}
          slots={{
            item: CustomTreeItem,
          }}
          sx={{
            backgroundColor: 'transparent',
            cursor: 'default',
            // 各状態の背景色を透明に上書き、元の挙動に合わせる
            '& .MuiTreeItem-content': {
              '&:hover': {
                backgroundColor: 'transparent',
              },
              '&.Mui-selected': {
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                '&.Mui-focused': {
                  backgroundColor: 'transparent',
                },
              },
              '&.Mui-focused': {
                backgroundColor: 'transparent',
              },
            },
            // 展開アイコン（>）部分だけを指マークにする
            '& .MuiTreeItem-iconContainer': {
              cursor: 'pointer',
            },
            // チェックボックス部分も指マークにする
            '& .MuiTreeItem-checkbox': {
              cursor: 'pointer',
            },
            // ラベル部分（テキストやカスタムアイコン）も念のためデフォルトカーソルに
            '& .MuiTreeItem-label': {
              cursor: 'default',
            },
          }}
        />
      )}
    </>
  )
}

export default PlaceGroupsTree
